import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Breadcrumbs,
  Link,
  Button,
} from "@mui/material";
import { Notifications, Settings, Home, ArrowBack } from "@mui/icons-material";

const TOP_NAV_HEIGHT = 64;

const ContractorTopNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);
  const ppState = useSelector((state) => state[currentSession]?.pp);

  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);

  const unreadNotifications = ppState?.ppNotifications?.unreadCount || 0;

  // Generate breadcrumbs from current path
  const generateBreadcrumbs = () => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const breadcrumbs = [
      {
        label: "My Permits",
        path: "/pp/contractor/permits",
        icon: <Home fontSize="small" />,
      },
    ];

    if (pathSegments.length > 2) {
      // Map path segments to readable names
      const segmentMap = {
        permits: "Permits",
        inspections: "Inspections",
        uploads: "Upload Corrections",
        messaging: "Messages",
      };

      let currentPath = "";
      pathSegments.forEach((segment, index) => {
        currentPath += `/${segment}`;
        if (segment !== "contractor" && segmentMap[segment]) {
          breadcrumbs.push({
            label: segmentMap[segment],
            path: currentPath,
          });
        }
      });
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleLogout = () => {
    // Implementation for logout
    handleProfileMenuClose();
  };

  return (
    <>
      <AppBar
        position="static"
        sx={{
          backdropFilter: "blur(6px)",
        }}
      >
        <Toolbar
          sx={{
            minHeight: TOP_NAV_HEIGHT,
            px: 2,
          }}
        >
          <Button
            color="inherit"
            startIcon={<ArrowBack />}
            onClick={() => navigate("/pp")}
            sx={{ mr: 2 }}
          >
            Back to PP Portal
          </Button>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Contractor Portal
          </Typography>

          {/* Notifications */}
          <IconButton
            size="large"
            color="inherit"
            onClick={handleNotificationMenuOpen}
          >
            <Badge badgeContent={unreadNotifications} color="error">
              <Notifications />
            </Badge>
          </IconButton>

          {/* Settings */}
          <IconButton
            size="large"
            color="inherit"
            onClick={() => navigate("/pp/contractor/settings")}
          >
            <Settings />
          </IconButton>

          {/* Profile Menu */}
          <IconButton
            size="large"
            edge="end"
            color="inherit"
            onClick={handleProfileMenuOpen}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.dark" }}>
              {currentUser?.user?.firstName?.[0] || "C"}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Breadcrumbs */}
      <Box
        sx={{
          px: 3,
          py: 1,
          backgroundColor: "grey.50",
          borderBottom: "2px solid #e0e0e0",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
        }}
      >
        <Breadcrumbs aria-label="breadcrumb">
          {breadcrumbs.map((crumb, index) =>
            index === breadcrumbs.length - 1 ? (
              <Typography
                key={crumb.path}
                color="text.primary"
                sx={{ display: "flex", alignItems: "center" }}
              >
                {crumb.icon && <Box sx={{ mr: 0.5 }}>{crumb.icon}</Box>}
                {crumb.label}
              </Typography>
            ) : (
              <Link
                key={crumb.path}
                underline="hover"
                color="inherit"
                href={crumb.path}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(crumb.path);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  "&:hover": { color: "primary.main" },
                }}
              >
                {crumb.icon && <Box sx={{ mr: 0.5 }}>{crumb.icon}</Box>}
                {crumb.label}
              </Link>
            )
          )}
        </Breadcrumbs>
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => {
            navigate("/pp/contractor/settings");
            handleProfileMenuClose();
          }}
        >
          Settings
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate("/profilesettings");
            handleProfileMenuClose();
          }}
        >
          Profile
        </MenuItem>
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </Menu>

      {/* Notification Menu */}
      <Menu
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: { width: 320, maxHeight: 400 },
        }}
      >
        <MenuItem
          onClick={() => {
            navigate("/pp/contractor/notifications");
            handleNotificationMenuClose();
          }}
        >
          <Typography variant="body2" color="primary">
            View All Notifications ({unreadNotifications} unread)
          </Typography>
        </MenuItem>
        {ppState?.ppNotifications?.data?.slice(0, 3).map((notification) => (
          <MenuItem key={notification.id} onClick={handleNotificationMenuClose}>
            <Box>
              <Typography
                variant="body2"
                fontWeight={notification.read ? "normal" : "bold"}
              >
                {notification.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {notification.message.length > 50
                  ? `${notification.message.substring(0, 50)}...`
                  : notification.message}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ContractorTopNav;
